import React, { createContext, useCallback, useEffect, useRef, useState } from 'react'
import { useAppDispatch } from '../app/hooks'
import { useAuthContext } from '../auth/AuthContext'
import { wsConnected, wsMessage } from '../features/environment/environmentSlice'
import { isEmptyString } from '../misc/utils/utils'
import keycloak from '../keycloak'
import { createWsReconnector, Reconnector, WsLike } from './wsReconnect'

export interface WsContextType {
  sendMessage: (msg: any) => void,
  connected: Boolean
}

const initialValue = {
  sendMessage: () => {
  },
  connected: false
}

const WebSocketContext = createContext<WsContextType>(initialValue)
export const useWebSocketContext = () => React.useContext(WebSocketContext)

// Refresh the token when it is within this many seconds of expiry.
const MIN_TOKEN_VALIDITY_SECONDS = 30

const buildWsUrl = (token: string): string => {
  const host = window.location.host.includes('localhost') ? 'localhost:9002' : window.location.host
  const proto = window.location.host.includes('localhost') ? 'ws' : 'wss'
  return `${proto}://${host}/ws?auth_token=${token}`
}

export const WebSocketProvider: React.FC = ({ children }) => {
  const auth = useAuthContext()
  const dispatch = useAppDispatch()
  const [connected, setConnected] = useState<Boolean>(false)

  // The freshest token, kept in a ref so the reconnector always reads the
  // CURRENT value (Keycloak's onTokens refresh updates auth.token → this ref)
  // instead of a value captured when the socket was first opened.
  const tokenRef = useRef<string>(auth?.token ?? '')
  const socketRef = useRef<WsLike | undefined>()
  const reconnectorRef = useRef<Reconnector | undefined>()

  useEffect(() => {
    tokenRef.current = auth?.token ?? ''
  }, [auth?.token])

  // Send message to websocket. Memoized so consumers can safely use it in
  // effect dependencies; only sends on an OPEN socket (send() on a CONNECTING
  // socket throws).
  const sendMessage = useCallback((msg: any) => {
    const socket = socketRef.current as WebSocket | undefined
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify(msg))
    }
  }, [])

  // Build the reconnector once. It reads the current token on every attempt and
  // refreshes via Keycloak when the token is expired/near-expiry.
  useEffect(() => {
    const reconnector = createWsReconnector({
      buildUrl: buildWsUrl,
      createSocket: (url: string) => {
        const socket = new WebSocket(url)
        socketRef.current = socket as unknown as WsLike
        return socket as unknown as WsLike
      },
      getToken: () => tokenRef.current,
      isTokenValid: (token: string) =>
        !isEmptyString(token) && !keycloak.isTokenExpired(MIN_TOKEN_VALIDITY_SECONDS),
      refreshToken: async () => {
        // updateToken only actually refreshes when within minValidity of expiry;
        // it rejects when the refresh token is also expired (session gone).
        await keycloak.updateToken(MIN_TOKEN_VALIDITY_SECONDS)
        const fresh = keycloak.token ?? ''
        tokenRef.current = fresh
        return fresh
      },
      onConnectedChange: (isConnected: boolean) => {
        setConnected(isConnected)
        dispatch(wsConnected(isConnected))
      },
      onMessage: (payload: any) => dispatch(wsMessage(payload)),
      onGiveUp: () => {
        // Session is truly gone — surface disconnected and route to login.
        console.warn('WS: token refresh failed, redirecting to login')
        keycloak.login()
      },
      log: (...args: any[]) => console.log(...args),
    })
    reconnectorRef.current = reconnector
    return () => reconnector.stop()
  }, [dispatch])

  // Start once we have a token; stop when it clears (logout). start()/stop()
  // are idempotent, and the reconnector re-reads the token itself, so token
  // refreshes do NOT tear down a healthy socket.
  useEffect(() => {
    const reconnector = reconnectorRef.current
    if (!reconnector) return
    if (!isEmptyString(auth?.token)) {
      reconnector.start()
    } else {
      reconnector.stop()
    }
  }, [auth?.token])

  return (
    <WebSocketContext.Provider value={{ sendMessage, connected }}>
      {children}
    </WebSocketContext.Provider>
  )
}
